import {
  forwardRef, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type PropsWithChildren, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, LoaderCircle, X } from "lucide-react";
import { errorMessage } from "../lib/format";
import { WlsLogo } from "./WlsLogo";

export function Button({
  variant = "primary", loading, disabled, children, className = "", type = "button", ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "danger" | "ghost"; loading?: boolean }) {
  return (
    <button {...props} type={type} className={`button button--${variant} ${className}`} disabled={loading || disabled} aria-busy={loading || undefined}>
      {loading && <LoaderCircle size={16} className="spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Field({ label, hint, children }: PropsWithChildren<{ label: string; hint?: string }>) {
  return <label className="field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = "", ...props }, ref) {
    return <input ref={ref} {...props} className={`input ${className}`} />;
  },
);

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div><h1>{title}</h1>{description && <p className="page-description">{description}</p>}</div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Card({ children, className = "" }: PropsWithChildren<{ className?: string }>) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Badge({ children, tone = "neutral", title }: PropsWithChildren<{ tone?: "neutral" | "rose" | "danger" | "success" | "warning" | "evasion"; title?: string }>) {
  return <span className={`badge badge--${tone}`} title={title}>{children}</span>;
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-state__mark"><WlsLogo size={34} /></div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

/**
 * Modale accessible : ouverture au clavier, fermeture par Échap, focus piégé puis restitué,
 * et rendu hors du flux pour ne jamais passer sous l'en-tête collant.
 * `dismissible={false}` retire toute fermeture accidentelle (clic de fond, Échap, croix) :
 * indispensable quand le contenu ne peut plus être réaffiché, comme une clé API.
 */
export function Modal({
  title, description, onClose, dismissible = true, className = "", children,
}: PropsWithChildren<{ title: string; description?: string; onClose: () => void; dismissible?: boolean; className?: string }>) {
  const panel = useRef<HTMLElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea, button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissible) { event.stopPropagation(); onClose(); return; }
      if (event.key !== "Tab" || !panel.current) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previous?.focus?.();
    };
  }, [onClose, dismissible]);

  return createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (dismissible && event.target === event.currentTarget) onClose(); }}
    >
      <section ref={panel} className={`modal ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}>
        <header>
          <div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div>
          {dismissible && <button type="button" className="icon-button" onClick={onClose} aria-label="Fermer"><X size={20} /></button>}
        </header>
        {children}
      </section>
    </div>,
    document.body,
  );
}

/** Confirmation d'une action irréversible, dans le style de l'application plutôt qu'en boîte système. */
export function ConfirmDialog({
  title, description, target, confirmLabel = "Confirmer", tone = "danger", onConfirm, onClose,
}: {
  title: string; description: string; target?: string; confirmLabel?: string;
  tone?: "danger" | "primary"; onConfirm: () => Promise<unknown> | unknown; onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const confirm = async () => {
    setLoading(true); setError("");
    // `errorMessage` extrait le message applicatif : afficher `reason.message` exposerait
    // la trace serveur (chemin de fichier et ligne) à l'utilisateur.
    try { await onConfirm(); onClose(); }
    catch (reason) { setError(errorMessage(reason)); setLoading(false); }
  };
  return (
    <Modal title={title} description={description} onClose={onClose}>
      {target && <div className="confirm-target"><AlertTriangle size={17} /><span>{target}</span></div>}
      {error && <Notice tone="error">{error}</Notice>}
      <div className="modal-actions">
        <Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button variant={tone} loading={loading} onClick={() => void confirm()}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}

const NOTICE_ICONS = { info: Info, error: AlertCircle, success: CheckCircle2, warning: AlertTriangle };

/** Le ton se lit à l'icône autant qu'à la couleur : un daltonien distingue ainsi une erreur d'un succès. */
export function Notice({ tone = "info", children }: PropsWithChildren<{ tone?: "info" | "error" | "success" | "warning" }>) {
  const Icon = NOTICE_ICONS[tone];
  return (
    <div className={`notice notice--${tone}`} role={tone === "error" ? "alert" : "status"}>
      <Icon size={16} className="notice__icon" aria-hidden="true" />
      <div className="notice__body">{children}</div>
    </div>
  );
}

export function FullScreenLoader({ label }: { label: string }) {
  return <div className="fullscreen-loader" role="status"><LoaderCircle className="spin" size={28} /><span>{label}</span></div>;
}

export function TableSkeleton() {
  return <div className="skeleton-list" role="status" aria-busy="true" aria-label="Chargement en cours"><div /><div /><div /></div>;
}

/** Barre de progression annoncée aux technologies d'assistance. */
export function ProgressBar({ value, total, label }: { value: number; total: number; label: string }) {
  const percent = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="progress" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <span><i style={{ transform: `scaleX(${percent / 100})` }} /></span>
      <small>{value} / {total} · {percent}%</small>
    </div>
  );
}
